import {
  SIZE_SYSTEMS,
  sizesForSystem,
  convertToUk,
  ukFromMeasurement,
  formatUkLetter,
} from '@amada/ring-size-data';

const UNKNOWN = "I don't know";
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function youtubeId(url) {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) return parsed.pathname.slice(1);
    if (parsed.searchParams.get('v')) return parsed.searchParams.get('v');
    const parts = parsed.pathname.split('/').filter(Boolean);
    const i = parts.indexOf('embed');
    return i >= 0 ? parts[i + 1] || '' : '';
  } catch {
    return '';
  }
}

class AmadaRingSizeGuide extends HTMLElement {
  #dialog = null;
  #lastFocus = null;
  #convertRow = null;
  #measureRow = null;
  #onKeyDown = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.#dialog.addEventListener('cancel', this.#onCancel);
    this.#fillSizeList('us');
    if (!this.dataset.videoUrl) this.querySelector('[data-rsg-video]')?.classList.add('is-empty');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onInput);
    this.removeEventListener('submit', this.#onSubmit);
    this.#dialog?.removeEventListener('cancel', this.#onCancel);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  open() {
    if (!this.#dialog || this.#dialog.open) return;
    this.#lastFocus = document.activeElement;
    this.#showScreen('choice');
    this.#dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.#dialog.querySelector('[data-rsg-close]')?.focus();
    this.#onKeyDown = (event) => this.#trapFocus(event);
    document.addEventListener('keydown', this.#onKeyDown);
  }

  close() {
    if (!this.#dialog?.open) return;
    this.#unloadVideo();
    document.removeEventListener('keydown', this.#onKeyDown);
    document.documentElement.removeAttribute('scroll-lock');
    this.#dialog.close();
    this.#lastFocus?.focus?.();
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  #market() {
    const fromGuide = (this.dataset.sizeMarket || '').toLowerCase();
    if (fromGuide === 'us' || fromGuide === 'eu' || fromGuide === 'uk') return fromGuide;
    const fromPage = (document.documentElement.getAttribute('data-amada-market') || '').toLowerCase();
    if (fromPage === 'us' || fromPage === 'eu') return fromPage;
    return 'uk';
  }

  #systemLabel(market = this.#market()) {
    if (market === 'us') return 'US';
    if (market === 'eu') return 'EU';
    return 'UK';
  }

  #displaySize(row) {
    if (!row) return '';
    const market = this.#market();
    if (market === 'us') return String(row.us || '').trim();
    if (market === 'eu') return String(row.eu || '').trim();
    return formatUkLetter(row.uk) || row.uk;
  }

  #useConvertedSize(row) {
    if (!row) return;
    const uk = formatUkLetter(row.uk) || row.uk;
    const size = this.#displaySize(row) || uk;
    this.#emit('amada:ring-size-selected', { uk: size, size, system: this.#market(), row });
    this.close();
  }

  #onClick = (event) => {
    if (event.target.closest('[data-rsg-close]') || event.target === this.#dialog) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-rsg-confirm-later]')) {
      event.preventDefault();
      this.#emit('amada:ring-size-unknown', { value: UNKNOWN });
      this.close();
      return;
    }
    const goto = event.target.closest('[data-rsg-goto]');
    if (goto) {
      event.preventDefault();
      this.#showScreen(goto.dataset.rsgGoto);
      return;
    }
    if (event.target.closest('[data-rsg-play]')) {
      event.preventDefault();
      this.#loadVideo();
      return;
    }
    if (event.target.closest('[data-rsg-use-convert]')) {
      event.preventDefault();
      this.#useConvertedSize(this.#convertRow);
      return;
    }
    if (event.target.closest('[data-rsg-edit-measure]')) {
      event.preventDefault();
      this.#editMeasure();
      return;
    }
    if (event.target.closest('[data-rsg-use-measure]')) {
      event.preventDefault();
      this.#useConvertedSize(this.#measureRow);
    }
  };

  #onInput = (event) => {
    if (event.target.matches('[data-rsg-system]')) {
      this.#fillSizeList(event.target.value);
      this.#runConvert();
    } else if (event.target.matches('[data-rsg-size-input]')) {
      this.#runConvert();
    } else if (event.target.matches('[data-rsg-measure-input], [data-rsg-unit]')) {
      const button = this.querySelector('[data-rsg-calculate]');
      if (button) button.disabled = !this.querySelector('[data-rsg-measure-input]')?.value.trim();
    }
  };

  #onSubmit = (event) => {
    if (!event.target.matches('[data-rsg-measure-form]')) return;
    event.preventDefault();
    this.#runMeasure();
  };

  #showScreen(name) {
    this.querySelectorAll('[data-rsg-screen]').forEach((screen) => {
      const active = screen.dataset.rsgScreen === name;
      screen.hidden = !active;
      screen.classList.toggle('is-active', active);
    });
    if (name !== 'measure') this.#unloadVideo();
    else this.#resetMeasureForm();
    this.#dialog?.querySelector('.amada-rsg__panel')?.scrollTo({ top: 0 });
  }

  #resetMeasureForm() {
    const form = this.querySelector('[data-rsg-measure-form]');
    const result = this.querySelector('[data-rsg-measure-result]');
    const error = this.querySelector('[data-rsg-measure-error]');
    form?.removeAttribute('hidden');
    if (result) result.hidden = true;
    if (error) error.hidden = true;
    this.#measureRow = null;
    const button = this.querySelector('[data-rsg-calculate]');
    if (button) button.disabled = !this.querySelector('[data-rsg-measure-input]')?.value.trim();
  }

  #editMeasure() {
    const form = this.querySelector('[data-rsg-measure-form]');
    const input = this.querySelector('[data-rsg-measure-input]');
    const button = this.querySelector('[data-rsg-calculate]');
    form?.removeAttribute('hidden');
    if (button) button.disabled = !input?.value.trim();
    input?.focus();
    input?.select?.();
  }

  #fillSizeList(systemId) {
    const list = this.querySelector('[data-rsg-size-list]');
    const hint = this.querySelector('[data-rsg-size-hint]');
    const system = SIZE_SYSTEMS[systemId] || SIZE_SYSTEMS.us;
    if (hint) hint.textContent = system.hint;
    if (!list) return;
    list.replaceChildren(
      ...sizesForSystem(system.id).map((value) => {
        const option = document.createElement('option');
        option.value = value;
        return option;
      })
    );
  }

  #runConvert() {
    const system = this.querySelector('[data-rsg-system]')?.value || 'us';
    const value = this.querySelector('[data-rsg-size-input]')?.value || '';
    const result = this.querySelector('[data-rsg-convert-result]');
    const error = this.querySelector('[data-rsg-convert-error]');
    if (!value.trim()) {
      this.#convertRow = null;
      if (result) result.hidden = true;
      if (error) error.hidden = true;
      return;
    }
    const row = convertToUk(system, value);
    this.#convertRow = row;
    if (!row) {
      if (result) result.hidden = true;
      if (error) error.hidden = false;
      return;
    }
    const size = this.#displaySize(row);
    if (error) error.hidden = true;
    if (result) result.hidden = false;
    const letter = this.querySelector('[data-rsg-convert-letter]');
    const inline = this.querySelector('[data-rsg-convert-letter-inline]');
    const cta = this.querySelector('[data-rsg-use-convert]');
    if (letter) letter.textContent = size;
    if (inline) inline.textContent = size;
    if (cta) cta.textContent = `Use ${this.#systemLabel()} size ${size}`;
  }

  #runMeasure() {
    const value = this.querySelector('[data-rsg-measure-input]')?.value || '';
    const unit = this.querySelector('[data-rsg-unit]')?.value || 'mm';
    const result = this.querySelector('[data-rsg-measure-result]');
    const error = this.querySelector('[data-rsg-measure-error]');
    const row = ukFromMeasurement(value, unit);
    this.#measureRow = row;
    if (!row) {
      if (result) result.hidden = true;
      if (error) error.hidden = false;
      this.querySelector('[data-rsg-measure-form]')?.removeAttribute('hidden');
      return;
    }
    if (error) error.hidden = true;
    if (result) result.hidden = false;
    this.querySelector('[data-rsg-measure-form]')?.setAttribute('hidden', '');
    const uk = formatUkLetter(row.uk) || row.uk;
    const letter = this.querySelector('[data-rsg-measure-letter]');
    const used = this.querySelector('[data-rsg-measure-used]');
    if (letter) letter.textContent = this.#displaySize(row) || uk;
    if (used) used.textContent = `${row.measurementMm} mm`;
  }

  #loadVideo() {
    const url = this.dataset.videoUrl;
    const slot = this.querySelector('[data-rsg-video-slot]');
    const wrap = this.querySelector('[data-rsg-video]');
    if (!url || !slot || slot.childElementCount) return;
    wrap?.classList.add('is-playing');
    const id = youtubeId(url);
    if (id) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1`;
      iframe.title = 'Measure your finger';
      iframe.allow = 'autoplay; encrypted-media';
      iframe.allowFullscreen = true;
      slot.append(iframe);
      return;
    }
    const video = document.createElement('video');
    video.src = url;
    video.controls = true;
    video.autoplay = true;
    slot.append(video);
  }

  #unloadVideo() {
    this.querySelector('[data-rsg-video-slot]')?.replaceChildren();
    this.querySelector('[data-rsg-video]')?.classList.remove('is-playing');
  }

  #trapFocus(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.#dialog?.open) return;
    const nodes = [...this.#dialog.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[hidden]'));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('amada-ring-size-guide')) {
  customElements.define('amada-ring-size-guide', AmadaRingSizeGuide);
}
